import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api';
import PeriodToolbar from '../components/analytics/PeriodToolbar';
import MetricBarChart from '../components/analytics/MetricBarChart';
import PageHeader from '../components/ui/PageHeader';
import PageCard from '../components/ui/PageCard';
import Loader from '../components/ui/Loader';
import ErrorMessage from '../components/ui/ErrorMessage';
import { computeSalesKpis, enrichSummaryItems } from '../utils/metricTags';
import '../components/analytics/Analytics.scss';
import './DashboardPage.scss';

export default function DashboardPage() {
    const [periodDays, setPeriodDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const result = await api.getDashboard(periodDays);
            setData(result);
        } catch (err) {
            setError(err.message || 'Не удалось загрузить дашборд');
        } finally {
            setLoading(false);
        }
    }, [periodDays]);

    useEffect(() => {
        load();
    }, [load]);

    const metrics = useMemo(
        () => enrichSummaryItems(data?.metrics_summary || []).slice(0, 10),
        [data?.metrics_summary],
    );

    const salesKpis = useMemo(
        () => computeSalesKpis(data?.metrics_summary || []),
        [data?.metrics_summary],
    );

    if (loading && !data) {
        return <Loader text="Загружаем дашборд..." />;
    }

    const counts = data?.counts || {};
    const servers = data?.servers || [];

    return (
        <div className="dashboard-page">
            <PageHeader
                title="Дашборд"
                subtitle={`Обзор за ${periodDays} дней`}
            />

            <PeriodToolbar
                periodDays={periodDays}
                onPeriodChange={setPeriodDays}
                showCustomDates={false}
            />

            <ErrorMessage message={error} onRetry={load} />

            <div className="bento-grid bento-grid--3 dashboard-page__stats">
                <div className="stat-card">
                    <span className="stat-card__value">{counts.customers ?? 0}</span>
                    <span className="stat-card__label">Клиентов</span>
                </div>
                <div className="stat-card">
                    <span className="stat-card__value">{counts.active_subscriptions ?? 0}</span>
                    <span className="stat-card__label">Активных подписок</span>
                </div>
                <div className="stat-card">
                    <span className="stat-card__value">{counts.payments_today ?? 0}</span>
                    <span className="stat-card__label">Платежей сегодня</span>
                </div>
                <div className="stat-card">
                    <span className="stat-card__value">{counts.pending_payments ?? 0}</span>
                    <span className="stat-card__label">Ожидают оплаты</span>
                </div>
            </div>

            <div className="dashboard-page__grid">
                <PageCard className="dashboard-page__chart">
                    <h3 className="dashboard-page__section-title">Популярные действия</h3>
                    <MetricBarChart data={metrics} />
                    <div className="analytics-sales-hint">
                        <div className="analytics-sales-hint__item">
                            <span className="analytics-sales-hint__value">{salesKpis.initiated}</span>
                            <span className="analytics-sales-hint__label">Начато оплат</span>
                        </div>
                        <div className="analytics-sales-hint__item">
                            <span className="analytics-sales-hint__value">{salesKpis.confirmed}</span>
                            <span className="analytics-sales-hint__label">Подтверждено</span>
                        </div>
                    </div>
                    <p className="dashboard-page__chart-link">
                        <Link to="/metrics#sales">Подробнее в аналитике →</Link>
                    </p>
                </PageCard>

                <PageCard className="dashboard-page__servers">
                    <h3 className="dashboard-page__section-title">Серверы</h3>
                    {servers.length === 0 ? (
                        <p className="dashboard-page__empty">Серверы не настроены</p>
                    ) : (
                        <div className="dashboard-page__server-list">
                            {servers.map((server) => (
                                <div key={server.id} className="dashboard-page__server-card">
                                    <div className="dashboard-page__server-head">
                                        <strong>{server.domain || server.ip}</strong>
                                        <span>{server.utilization_percent}%</span>
                                    </div>
                                    <div className="dashboard-page__server-bar">
                                        <div
                                            className="dashboard-page__server-fill"
                                            style={{ width: `${Math.min(100, server.utilization_percent)}%` }}
                                        />
                                    </div>
                                    <p>
                                        {server.seats_occupied}
                                        {' '}
                                        /
                                        {server.seats}
                                        {' '}
                                        мест
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </PageCard>
            </div>
        </div>
    );
}
